/**
 * Backoffice DI Registry
 *
 * App-specific dependency registration.
 * This file should be imported FIRST in app.ts to initialize
 * the DI container before any components load.
 */

import { DiContainer } from "ui-common/di";

import { CounterState } from "ui-common/services/counter/counter-state";
import { CounterService } from "ui-common/services/counter/counter-service";
import { SseState } from "ui-common/services/sse/sse-state";
import { SseService } from "ui-common/services/sse/sse-service";
import { RpcService } from "ui-common/services/rpc/rpc-service";
import { LiveChatState } from "ui-common/services/live-chat/chat-state";
import { LiveChatService } from "ui-common/services/live-chat/chat-service";
import type { ISseState } from "ui-common/services/sse/types";
import type { ILiveChatState } from "ui-common/services/live-chat/types";

/**
 * Type-safe key constants.
 * Use these when registering and retrieving dependencies.
 */
export const KEYS = {
  counterState: "counterState",
  counterService: "counterService",
  sseState: "sseState",
  writableSseState: "writableSseState",
  sseService: "sseService",
  rpcService: "rpcService",
  liveChatState: "liveChatState",
  writableLiveChatState: "writableLiveChatState",
  liveChatService: "liveChatService",
} as const;

/**
 * Initialize the DI container with all dependencies.
 * Call this FIRST in app.ts before any other imports that use injection.
 */
export function initializeDi(): void {
  // Create instances
  const counterState = new CounterState();
  const counterService = new CounterService();
  const sseState = new SseState();
  const sseService = new SseService();
  const rpcService = new RpcService();
  const liveChatState = new LiveChatState();
  const liveChatService = new LiveChatService();

  // Initialize container with all dependencies
  DiContainer.initialize({
    [KEYS.counterState]: counterState,
    [KEYS.counterService]: counterService,
    [KEYS.sseState]: sseState as ISseState,
    [KEYS.writableSseState]: sseState,
    [KEYS.sseService]: sseService,
    [KEYS.rpcService]: rpcService,
    [KEYS.liveChatState]: liveChatState as ILiveChatState,
    [KEYS.writableLiveChatState]: liveChatState,
    [KEYS.liveChatService]: liveChatService,
  });

  // Initialize services that need it
  counterService.initialize();
  sseService.initialize();

  console.log("[DI] Backoffice container initialized with keys:", DiContainer.keys());
}
